import { useMemo, useState } from "react";

import {
    Icon,
    InvisibleSelect,
    Row,
    SelectOption,
    StatProps,
    Text,
    TextColor,
    TextVariant,
    Tooltip
} from "@bridgesplit/ui";
import { formatNum, formatPercent, TIME } from "@bridgesplit/utils";
import { TokenListMetadata } from "@bridgesplit/abf-sdk";
import {
    LoopscalePointsAction,
    PriceFetchType,
    useOraclePrices,
    useLoanMultipliersUtil,
    useRewardsPointsPerSecond,
    LoanMultiplier,
    RewardPointsPerSecond
} from "@bridgesplit/abf-react";
import { COPY } from "app/constants";

import { getLoopscalePointsPerUnitTime } from "./util";
import { OracleTooltipContent, ORACLE_DISCREPANCY_THRESHOLD } from "../common";
import { useRewardsColor } from "./app/util";

const TIME_OPTIONS: SelectOption<number>[] = [
    { label: "per day", value: TIME.DAY },
    { label: "per week", value: TIME.WEEK },
    { label: "per month", value: TIME.MONTH },
    { label: "per year", value: TIME.YEAR }
];
export interface LoopscalePointStatProps {
    variant?: TextVariant;
    textSelectColor?: TextColor;
    action: LoopscalePointsAction;
    showSelect?: boolean;
    totalPrincipalAmountUsd: number | undefined;
    identifier: string | undefined;
}

/**
 * Hook to calculate generate daily reward stat for a given loopscale action and metadata
 */
export function useLoopscalePointStat({
    variant = "body2",
    textSelectColor = "secondary",
    action,
    totalPrincipalAmountUsd,
    identifier
}: LoopscalePointStatProps): StatProps {
    const [estimateSeconds, setEstimateSeconds] = useState(TIME_OPTIONS[0].value);

    const { loanMultipliers } = useLoanMultipliersUtil();
    const { rewardPointsPerSecond } = useRewardsPointsPerSecond();
    const color = useRewardsColor(loanMultipliers, identifier ? [identifier] : undefined, [action]);

    const calculatedPoints = useMemo(() => {
        const pointsBasedOnLoopscaleAction = getLoopscalePointsPerUnitTime(
            action,
            identifier,
            estimateSeconds,
            totalPrincipalAmountUsd,
            rewardPointsPerSecond,
            loanMultipliers
        );
        return pointsBasedOnLoopscaleAction;
    }, [action, identifier, estimateSeconds, totalPrincipalAmountUsd, rewardPointsPerSecond, loanMultipliers]);

    const props: LoopscalePointStatInfoProps = {
        action,
        totalPrincipalAmountUsd,
        calculatedPoints,
        variant,
        color,
        estimateSeconds,
        setEstimateSeconds,
        loanMultipliers,
        rewardPointsPerSecond,
        identifier,
        textSelectColor
    };

    return {
        value: getLoopscalePointsStatValue(props),
        caption: getLoopscalePointsStatCaption(props)
    };
}

interface LoopscalePointStatInfoProps extends LoopscalePointStatProps {
    loanMultipliers: LoanMultiplier | undefined;
    rewardPointsPerSecond: RewardPointsPerSecond | undefined;
    estimateSeconds: number;
    setEstimateSeconds: React.Dispatch<React.SetStateAction<number>>;
    color: string;
    calculatedPoints: number | undefined;
}

function getLoopscalePointsStatValue({ calculatedPoints, variant, color }: LoopscalePointStatInfoProps) {
    return (
        <Row spacing={1}>
            <Text sx={{ color }}>
                <Icon type="points" />
            </Text>
            <Text variant={variant} loading={!calculatedPoints}>
                {formatNum(calculatedPoints)}
            </Text>
        </Row>
    );
}

function getLoopscalePointsStatCaption({
    variant,
    estimateSeconds,
    setEstimateSeconds,
    textSelectColor
}: LoopscalePointStatInfoProps) {
    return (
        <Row spacing={0.5}>
            <Text variant={variant} color="caption">
                Rewards
            </Text>
            <InvisibleSelect
                variant={variant}
                color={textSelectColor}
                value={estimateSeconds}
                setValue={setEstimateSeconds}
                options={TIME_OPTIONS}
            />
        </Row>
    );
}
/**
 * Hook to calculate generate Oracle discrepency stats
 */
export function useOracleDiscrepencyStats({
    metadata,
    variant
}: {
    metadata: TokenListMetadata[] | undefined;
    variant: TextVariant;
}): StatProps[] {
    const { getOracle } = useOraclePrices(
        metadata?.map((t) => t.mint),
        PriceFetchType.Twap
    );

    const stats: StatProps[] = [];

    if (metadata) {
        for (const token of metadata) {
            const jupiterPrice = getOracle(token.mint)?.marketPrice;
            const oraclePrice = getOracle(token.mint, PriceFetchType.Twap)?.usdPrice;
            if (jupiterPrice && oraclePrice) {
                const oracleDiff = (jupiterPrice - oraclePrice) / oraclePrice;
                if (Math.abs(oracleDiff) > ORACLE_DISCREPANCY_THRESHOLD) {
                    stats.push({
                        caption: COPY.LOOP_ORACLE_DISCREPANCY_TERM,
                        value: getOracleDiscrepancyValue(oracleDiff, token, true, variant)
                    });
                }
            }
        }
    }

    return stats;
}

function getOracleDiscrepancyValue(
    oracleDiff: number,
    token: TokenListMetadata,
    isWind: boolean,
    variant: TextVariant
) {
    const color = isWind ? (oracleDiff < 0 ? "success" : "warning") : oracleDiff > 0 ? "warning" : "success";

    return (
        <Tooltip
            title={
                <OracleTooltipContent
                    oracleDiff={oracleDiff}
                    collateralToken={token}
                    isWind={isWind}
                    variant={variant}
                />
            }
            reverseColors
        >
            <Row spacing={0.5}>
                <Text color="disabled">
                    <Icon type="tooltip" />
                </Text>
                <Text color={oracleDiff === 0 ? "caption" : color}>
                    {oracleDiff === 0 ? "No difference" : formatPercent(oracleDiff)}
                </Text>
            </Row>
        </Tooltip>
    );
}
